import clsx from 'clsx';
import { ArrowLeft, Eye, EyeOff, ExternalLink, FileText, Lock, MessagesSquare, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { studioApi, useStudioTasks, useStudioTeam, useStudioThreads, useUpdateTask } from '@/api/studio';
import type { Project, Task } from '@/api/types';
import { AttachmentChip } from '@/components/AttachmentChip';
import { Markdown } from '@/components/Markdown';
import { NewDiscussionDialog } from '@/components/NewDiscussionDialog';
import { QueryPending } from '@/components/QueryState';
import { Thread } from '@/components/Thread';
import { PersonLink } from '@/components/PersonDialog';
import { Avatar, Dialog, Empty, Status } from '@/components/ui';
import { formatDate, formatWhen } from '@/lib/format';
import { needsLabel, taskLabel } from '@/lib/status';

export function NeedsEditor({ t, onClose }: { t: Task; onClose: () => void }) {
  const update = useUpdateTask();
  const [kind, setKind] = useState<NonNullable<Task['needsClient']>['kind']>(t.needsClient?.kind ?? 'approve');
  const [note, setNote] = useState(t.needsClient?.note ?? '');
  return (
    <Dialog open onClose={onClose} title={`Ждём клиента: ${t.key}`}>
      <div className="space-y-4 p-5">
        <div className="flex flex-wrap gap-1.5">
          {(Object.keys(needsLabel) as (keyof typeof needsLabel)[]).map((k) => (
            <button key={k} className="chip" aria-pressed={kind === k} onClick={() => setKind(k)}>
              {needsLabel[k].replace('Ждёт ', '')}
            </button>
          ))}
        </div>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Что именно нужно — клиент увидит это первым</span>
          <input className="field" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Утвердите прототип или оставьте замечания" autoFocus />
        </label>
        <div className="flex justify-between gap-2 border-t border-line pt-4">
          {t.needsClient ? (
            <button className="btn-ghost" onClick={() => update.mutate({ id: t.id, patch: { needsClient: null } }, { onSuccess: onClose })}>
              Больше не ждём
            </button>
          ) : (
            <span />
          )}
          <button className="btn-primary" disabled={!note.trim()} onClick={() => update.mutate({ id: t.id, patch: { needsClient: { kind, note: note.trim() }, clientVisible: true } }, { onSuccess: onClose })}>
            Попросить клиента
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/** One task as the studio sees it: the card the client opens, plus visibility and «ждём клиента». */
export function StudioTask({ p, taskId }: { p: Project; taskId: string }) {
  const tasksQ = useStudioTasks(p.id);
  const teamQ = useStudioTeam();
  const { data: threads } = useStudioThreads({ projectId: p.id });
  const update = useUpdateTask();
  const navigate = useNavigate();
  const [needs, setNeeds] = useState(false);
  const [talking, setTalking] = useState(false);
  const back = `/studio/projects/${p.id}/tasks`;

  if (!tasksQ.data) return <QueryPending query={tasksQ} />;
  const t = tasksQ.data.find((x) => x.id === taskId || x.key.toLowerCase() === taskId.toLowerCase());
  if (!t) {
    return (
      <Empty title="Задача не найдена">
        <Link className="underline" to={back}>
          К списку задач
        </Link>
      </Empty>
    );
  }
  const s = taskLabel(t);
  const open = t.status !== 'done';
  const linked = threads?.filter((x) => x.taskId === t.id && x.id !== t.threadId) ?? [];
  const me = teamQ.data?.[0];

  return (
    <div>
      <Link to={back} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink">
        <ArrowLeft className="size-4" /> Все задачи
      </Link>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
        <article className="panel min-w-0 space-y-5 self-start p-5">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-faint">{t.key}</span>
              {t.linearUrl && (
                <a href={t.linearUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-muted hover:text-ink">
                  Открыть в Linear <ExternalLink className="size-3" />
                </a>
              )}
            </div>
            <h2 className="mt-1 text-lg font-semibold leading-snug tracking-tight">{t.title}</h2>
          </div>

          <dl className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
            <div className="flex items-center gap-1.5">
              <dt className="sr-only">Статус</dt>
              <dd>
                <Status tone={s.tone}>{s.label}</Status>
              </dd>
            </div>
            <div className="flex items-center gap-1.5 text-muted">
              <dt className="sr-only">Исполнитель</dt>
              <dd className="inline-flex items-center gap-1.5">
                <PersonLink person={t.assignee} className="inline-flex items-center gap-1.5 rounded hover:underline hover:underline-offset-2">
                  <Avatar person={t.assignee} size="sm" />
                  {t.assignee.name}
                </PersonLink>
              </dd>
            </div>
            {t.due && (
              <div className="text-muted">
                <dt className="inline">Срок </dt>
                <dd className="inline text-ink">{formatDate(t.due)}</dd>
              </div>
            )}
            <div className="text-muted">
              <dt className="inline">Этап </dt>
              <dd className="inline text-ink">{t.group}</dd>
            </div>
          </dl>

          <section aria-label="Видимость" className="flex flex-wrap items-center justify-between gap-3 rounded bg-panel px-4 py-3 text-sm shadow-hair">
            <div>
              <div className="flex items-center gap-1.5 font-medium">
                {t.clientVisible ? <Eye className="size-4" /> : <EyeOff className="size-4 text-faint" />}
                {t.clientVisible ? 'Клиент видит задачу' : 'Клиент не видит задачу'}
              </div>
              {t.visibleByLabel && (
                <div className="mt-0.5 flex items-center gap-1.5 text-muted">
                  <Lock className="size-3.5" /> Показана меткой portal_for_client в Linear: снять её можно только там.
                </div>
              )}
            </div>
            <button className="btn-secondary" disabled={t.visibleByLabel || update.isPending} onClick={() => update.mutate({ id: t.id, patch: { clientVisible: !t.clientVisible } })}>
              {t.clientVisible ? 'Скрыть от клиента' : 'Показать клиенту'}
            </button>
          </section>

          {open && (
            <section
              aria-label="Ждём клиента"
              className={clsx('flex flex-wrap items-center justify-between gap-3 rounded px-4 py-3 text-sm', t.needsClient ? 'bg-waiting/[0.08] shadow-[inset_0_0_0_1px_rgb(var(--waiting)/0.25)]' : 'bg-panel shadow-hair')}
            >
              <div>
                <div className="font-medium">{t.needsClient ? t.needsClient.note : 'Ничего от клиента не ждём'}</div>
                {t.needsClient && <div className="text-muted">{needsLabel[t.needsClient.kind]}</div>}
              </div>
              <button className="btn-secondary" onClick={() => setNeeds(true)}>
                {t.needsClient ? 'Изменить' : 'Попросить клиента'}
              </button>
            </section>
          )}

          {t.description ? <Markdown text={t.description} className="text-sm leading-relaxed" /> : <p className="text-sm text-muted">Описания нет.</p>}
          {t.attachments.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {t.attachments.map((a) => (
                <li key={a.id || a.name}>
                  <AttachmentChip a={a} load={studioApi.downloadDocument} icon={<FileText className="size-3.5 text-muted" />} className="inline-flex items-center gap-2 rounded bg-panel px-2.5 py-1.5 text-sm shadow-hair" />
                </li>
              ))}
            </ul>
          )}

          <section aria-label="Обсуждения по задаче" className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-medium text-muted">Отдельные обсуждения{linked.length > 0 ? `, ${linked.length}` : ''}</h3>
              <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setTalking(true)}>
                <Plus className="size-3.5" /> Новое
              </button>
            </div>
            {linked.length > 0 && (
              <ul className="divide-y divide-line overflow-hidden rounded shadow-hair">
                {linked.map((x) => (
                  <li key={x.id}>
                    <Link to={`/studio/projects/${p.id}/talk/${x.id}`} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-panel">
                      <MessagesSquare className="size-3.5 shrink-0 text-faint" />
                      <span className="min-w-0 flex-1 truncate font-medium">{x.title}</span>
                      <span className="num shrink-0 text-xs text-faint">{x.total}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <p className="text-xs text-faint">
            Создана {formatWhen(t.createdAt)}, обновлена {formatWhen(t.updatedAt)}
          </p>
        </article>

        <div className="panel flex h-[calc(100vh-220px)] min-h-[520px] min-w-0 flex-col overflow-hidden lg:sticky lg:top-[72px]">
          <div className="border-b border-line px-5 py-3">
            <div className="font-medium">Обсуждение задачи</div>
            <div className="text-xs text-muted">{t.clientVisible ? `Вы пишете как ${me?.name ?? '…'}, клиент увидит сообщения.` : 'Клиент не увидит это обсуждение, пока задача скрыта.'}</div>
          </div>
          {me ? <Thread key={t.threadId} threadId={t.threadId} as={me} placeholder={`Ответ клиенту по ${t.key}`} className="flex-1" /> : <QueryPending query={teamQ} compact />}
        </div>
      </div>
      {needs && <NeedsEditor t={t} onClose={() => setNeeds(false)} />}
      <NewDiscussionDialog studio open={talking} onClose={() => setTalking(false)} projectId={p.id} tasks={tasksQ.data} taskId={t.id} onCreated={(x) => navigate(`/studio/projects/${p.id}/talk/${x.id}`)} />
    </div>
  );
}
